import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  Rarity,
  RewardConfigRarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { Card } from '@sorare/core/src/components/card/Card';
import {
  ANY_SPORT_CARDS_SLUG,
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_SERIES_MARKET,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getSportFromSportOrShared } from '@sorare/core/src/lib/sports';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';
import type { CommonNotificationProps } from '#src/components/activity/Notification/types.ts';

import type { AutoClaimNotification_autoClaimNotification } from './__generated__/index.graphql.ts';

type Props = CommonNotificationProps & {
  notification: AutoClaimNotification_autoClaimNotification;
};

export const AutoClaimNotification = ({ notification, ...rest }: Props) => {
  const { generateSportContextPath } = useSportContext();

  const {
    createdAt,
    sport: sportOrShared,
    read,
    name,
    anyCard,
    cardShardRewardConfig,
  } = notification;
  const sport = getSportFromSportOrShared(sportOrShared) ?? Sport.FOOTBALL;
  const { configRarity } = cardShardRewardConfig || {};

  const cardLink = anyCard?.slug
    ? generateSportContextPath(ANY_SPORT_CARDS_SLUG, {
        params: {
          slug: anyCard.slug,
        },
        sport: anyCard.sport ?? Sport.FOOTBALL,
      })
    : undefined;

  let shardLink;
  if (
    (sport === Sport.FOOTBALL || sport === Sport.NBA) &&
    !!configRarity &&
    configRarity === Rarity.common
  ) {
    shardLink = generateSportContextPath(ANY_SPORT_SERIES_MARKET, {
      sport,
    });
  } else if (
    !!configRarity &&
    configRarity !== RewardConfigRarity.unknown_rarity
  ) {
    shardLink = generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
      params: {
        type: configRarity,
      },
      sport,
    });
  }

  return (
    <DumbNotification
      title={<LazyMarkdown data={notification.markdownBody} />}
      createdAt={createdAt}
      sport={sport}
      read={read}
      name={name}
      cardPicture={!!anyCard && <Card card={anyCard} />}
      link={cardLink || shardLink}
      {...rest}
    />
  );
};

AutoClaimNotification.fragments = {
  autoClaimNotification: gql`
    fragment AutoClaimNotification_autoClaimNotification on AutoClaimNotification {
      id
      ...Notification_notificationInterface
      anyCard {
        slug
        sport
        ...Card_anyCard
      }
      cardShardRewardConfig {
        id
        configRarity
      }
    }
    ${commonNotificationInterfaceFragment}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<AutoClaimNotification_autoClaimNotification>,
};
